import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { formResolver } from 'src/utils/formResolver';
import * as yup from 'yup';
import {
  booleanScheme,
  emailScheme,
  momentScheme,
  numberScheme,
  passwordScheme,
  stringScheme,
} from 'src/utils/validation-schemes';
import Input from 'src/components/Input/Input';
import { Button } from 'src/components/ui/button';
import { ZENDESK_HELP_CENTER_URL } from 'src/constants/urls';
import {
  useLazyGetKnowledgeBaseCollectorZendeskConsentLinkQuery,
  useUpdateKnowledgeBaseCollectorMutation,
} from 'src/services/knowledgeBaseService';
import { useParams } from 'react-router-dom';
import { useNotification } from 'src/hooks/useNotification';
import { useLoader } from 'src/provider/LoaderProvider';
import moment from 'moment';
import { Switch } from 'src/components/ui/switch';
import { RadioGroup, RadioGroupItem } from 'src/components/ui/radio-group';
import { TICKETS_INTERVAL } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/CurrentKnowledgeBaseConstants';
import { ISO_DATE_FORMAT, DATE_INPUT_FORMAT } from 'src/shared/constants';
import { InputInfoToolTip } from 'src/components/Input/InputInfoToolTip';
import { useAppSelector } from 'src/store';
import { getSubdomainHelper } from 'src/helpers/getSubdomainHelper';
import { Collapsible } from 'src/components/ui/collapsible';
import { Separator } from 'src/components/ui/separator';
import { Spinner } from 'src/components/Spinner/Spinner';
import {
  COLLAPSIBLE_CONTENT_CLASS_NAME,
  COLLAPSIBLE_TRIGGER_CLASS_NAME,
} from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/CreateZendeskIntegrationPopup/ZendeskIntegrationForm2/ZendeskHelpCenterFields/ZendeskConnectSection';
import ArrowLargeIcon from 'src/assets/img/arrow-large-icon.svg';
import ChevronDownIcon from 'src/assets/img/chevron-down-icon.svg';
import type { TreeNode } from 'src/utils/fileUtils';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';
import { useZendeskAuthorizationPolling } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/CreateZendeskIntegrationPopup/ZendeskIntegrationForm2/useZendeskAuthorizationPolling';

const INTERVAL_OPTIONS = [
  { value: TICKETS_INTERVAL.WEEKLY, id: 'interval-weekly', label: 'Weekly' },
  { value: TICKETS_INTERVAL.MONTHLY, id: 'interval-monthly', label: 'Monthly' },
];

const CalendarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 15 15" fill="none">
    <path
      d="M2 6.23077H13.0769M2 6.23077V3.15385H13.0769V6.23077M2 6.23077V13H13.0769V6.23077M4.46154 3.15385V1M10.6154 3.15385V1"
      stroke="#AEB0B6"
      strokeWidth="1.25"
    />
  </svg>
);

const PersonIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 15 15" fill="none">
    <path
      d="M7.5 3.375C8.25625 3.375 8.875 3.99375 8.875 4.75C8.875 5.50625 8.25625 6.125 7.5 6.125C6.74375 6.125 6.125 5.50625 6.125 4.75C6.125 3.99375 6.74375 3.375 7.5 3.375ZM7.5 10.25C9.35625 10.25 11.4875 11.1369 11.625 11.625H3.375C3.53313 11.13 5.65062 10.25 7.5 10.25ZM7.5 2C5.98062 2 4.75 3.23062 4.75 4.75C4.75 6.26937 5.98062 7.5 7.5 7.5C9.01937 7.5 10.25 6.26937 10.25 4.75C10.25 3.23062 9.01937 2 7.5 2ZM7.5 8.875C5.66437 8.875 2 9.79625 2 11.625V13H13V11.625C13 9.79625 9.33563 8.875 7.5 8.875Z"
      fill="#AEB0B6"
    />
  </svg>
);

const TagIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M13.3333 6.66602V5.33268H10.6666V2.66602H9.33329V5.33268H6.66663V2.66602H5.33329V5.33268H2.66663V6.66602H5.33329V9.33268H2.66663V10.666H5.33329V13.3327H6.66663V10.666H9.33329V13.3327H10.6666V10.666H13.3333V9.33268H10.6666V6.66602H13.3333ZM9.33329 9.33268H6.66663V6.66602H9.33329V9.33268Z"
      fill="#AEB0B6"
    />
  </svg>
);

const GroupIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M3.51221 9.85449H2.66064V12.8096H3.51221V14.0479H1.40674V8.61621H3.51221V9.85449ZM14.5923 13.21H4.81689V11.9707H14.5923V13.21ZM14.5923 10.6934H4.81689V9.45508H14.5923V10.6934ZM3.51221 3.18848H2.66064V6.14258H3.51221V7.38184H1.40674V1.94922H3.51221V3.18848ZM14.5923 6.54297H4.81689V5.30371H14.5923V6.54297ZM14.5923 4.02637H4.81689V2.78809H14.5923V4.02637Z"
      fill="#AEB0B6"
    />
  </svg>
);

const SummaryRow = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) => (
  <div className={'flex items-center gap-2 text-sm font-medium'}>
    {icon}
    <p className={'text-grey_450'}>
      {label}: <span className={'ml-1 text-grey_750'}>{value}</span>
    </p>
  </div>
);

interface ZendeskTicketsFormValues {
  subdomain: string;
  auth_email: string;
  api_token: string;
  // MM/DD/YYYY
  start_from: string;
  autoSync: boolean;
  interval: number;
}

export const EditKnowledgeBaseZendeskTicketsForm = ({
  item,
  knowledgeBaseId,
  close = () => {},
}: {
  // A knowledge base row, or a collector wrapped as one during onboarding; the collector is in `data`.
  item: Pick<TreeNode, 'id' | 'name' | 'type' | 'data'>;
  knowledgeBaseId?: number | string | null;
  close?: () => void;
}) => {
  const { id } = useParams();
  const notification = useNotification();
  const loader = useLoader();
  const [updateCollector, { isLoading: isLoadingUpdateCollector }] =
    useUpdateKnowledgeBaseCollectorMutation();
  // Separate instance so saving before "Connect with Zendesk" doesn't trigger the Save success
  // flow (notification and closing the modal).
  const [saveCollectorBeforeConnect] = useUpdateKnowledgeBaseCollectorMutation();
  const [getZendeskConsentLink] = useLazyGetKnowledgeBaseCollectorZendeskConsentLinkQuery();
  const { isInactivePlan, isTrialPlan } = useAppSelector((s) => s.planSlice);
  const kbId = knowledgeBaseId || id;
  const { isCheckingAuthStatus, pollZendeskAuthorization } = useZendeskAuthorizationPolling(kbId);
  // Kept in state so a completed Zendesk authorization shows without reopening the modal.
  // The row being edited; its collector (with the Zendesk auth state) is in `data`.
  const [collector, setCollector] = useState(item);
  const [isManualConnectOpen, setIsManualConnectOpen] = useState(false);
  const isZendeskAuthorized =
    collector?.data?.auth_type === 'oauth' && collector?.data?.auth_status === 'ok';
  // A collector connected with an API token shows its credentials directly, with Zendesk OAuth
  // offered as the alternative; otherwise the credentials live in the collapsible manual panel.
  const usesManualToken = collector?.data?.auth_type === 'api_token';
  const isManualConnectActive = usesManualToken || isManualConnectOpen;

  useLoaderWhile(isLoadingUpdateCollector);

  const {
    handleSubmit,
    control,
    watch,
    reset,
    formState: { isDirty, isValid },
  } = useForm<ZendeskTicketsFormValues>({
    defaultValues: {
      // Collectors connected via Zendesk OAuth have no stored email; '' keeps the inputs controlled.
      subdomain: item.data.subdomain ?? '',
      auth_email: item.data.auth_email ?? '',
      api_token: '',
      start_from: moment(item.data.start_from).format('MM/DD/YYYY'),
      autoSync: item.data.schedule.enabled,
      interval: item.data.schedule.interval,
    },
    resolver: formResolver(
      yup.lazy((_values, { context }) => {
        // The email is only needed when connecting manually instead of via Zendesk OAuth.
        const isManualRequired = Boolean(context?.isManualConnectActive);

        return yup.object({
          subdomain: stringScheme({ required: true }).label('Zendesk API Subdomain'),
          auth_email: emailScheme({
            required: isManualRequired,
            allowEmpty: !isManualRequired,
          }).label('Zendesk email'),
          api_token: passwordScheme({ required: false, min: 3, max: 100 }).label('Zendesk API Key'),
          start_from: momentScheme({ required: true, valid: true }),
          autoSync: booleanScheme({ required: false }).label('AutoSync'),
          interval: numberScheme({ required: false }).label('Interval'),
        });
      }),
    ),
    context: { isManualConnectActive },
  });

  const buildBody = (data: ZendeskTicketsFormValues) => {
    const body: Record<string, unknown> = {
      ...item.data,
      schedule: { ...item.data.schedule, enabled: data.autoSync, interval: data.interval },
      start_from: moment(data.start_from, DATE_INPUT_FORMAT).startOf('day').format(ISO_DATE_FORMAT),
      subdomain: data.subdomain,
    };

    // Credentials come from Zendesk OAuth unless the manual API token panel is used.
    if (isManualConnectActive) {
      body.auth_email = data.auth_email;
      if (data.api_token) {
        body.api_token = data.api_token;
      }
    } else {
      delete body.auth_email;
      delete body.api_token;
    }

    return body;
  };

  const openZendeskConsentPage = async () => {
    const { data } = await getZendeskConsentLink({
      knowledgeBaseId: kbId,
      collectorId: collector.id,
    });
    if (!data?.authorize_url) return false;

    window.open(data.authorize_url);
    return true;
  };

  // Zendesk authorizes the collector as stored, so unsaved changes (e.g. the subdomain) are
  // saved first. Resolves false when saving fails.
  const saveChangesBeforeConnect = async (formData: ZendeskTicketsFormValues) => {
    if (!isDirty) return true;

    loader.show();
    const { error } = await saveCollectorBeforeConnect({
      knowledgeBaseId: kbId,
      collectorId: collector.id,
      body: buildBody(formData),
    });
    loader.hide();

    if (error) return false;

    // The saved values become the new baseline, so Save is only enabled for later changes.
    reset(formData);
    return true;
  };

  const onConnectWithZendesk = async (formData: ZendeskTicketsFormValues) => {
    if (!(await saveChangesBeforeConnect(formData))) return;

    if (await openZendeskConsentPage()) {
      pollZendeskAuthorization(collector.id, (authorizedCollector) =>
        setCollector({ ...collector, data: authorizedCollector }),
      );
    }
  };

  // The collector is already authorized, so there is no status change to poll for.
  const onReconnectWithZendesk = async (formData: ZendeskTicketsFormValues) => {
    if (!(await saveChangesBeforeConnect(formData))) return;

    await openZendeskConsentPage();
  };

  // Run the same validation as submit; the connect handlers only fire for a valid form.
  const connectWithZendesk = handleSubmit(onConnectWithZendesk);
  const reconnectWithZendesk = handleSubmit(onReconnectWithZendesk);

  const submit = (data: ZendeskTicketsFormValues) => {
    if (isDirty && isValid) {
      updateCollector({ knowledgeBaseId: kbId, collectorId: item.id, body: buildBody(data) }).then(
        (updateCollectorResult) => {
          if (!('error' in updateCollectorResult)) {
            notification.success(`${item.name} Settings Updated Successfully.`);
            close();
          }
        },
      );
    }
  };

  const isAutoSync = watch('autoSync');

  const zendeskConnectButton = (
    <div className={'flex flex-col gap-2'}>
      {isZendeskAuthorized && (
        <output
          className={
            'block w-full rounded-lg bg-green_150 px-8 py-2 text-center text-[13px] font-medium text-green_750'
          }
        >
          Connected successfully with Zendesk
        </output>
      )}
      <Button
        type={'button'}
        variant={'dark'}
        onClick={isZendeskAuthorized ? reconnectWithZendesk : connectWithZendesk}
        className={'w-full px-8 h-[38px]'}
      >
        {isZendeskAuthorized ? 'Reconnect with Zendesk' : 'Connect with Zendesk'}
        <img src={ArrowLargeIcon} alt={''} width={16} height={16} />
      </Button>
      <p className={'text-center text-[13px] font-medium text-tertiary'}>
        You’ll be redirected to Zendesk to authorize access.
      </p>
    </div>
  );

  const orSeparator = (
    <div className={'flex items-center gap-2'}>
      <Separator className={'flex-1 bg-border_subtle'} />
      <span className={'text-[13px] font-medium text-tertiary'}>or</span>
      <Separator className={'flex-1 bg-border_subtle'} />
    </div>
  );

  const manualCredentialFields = (
    <div className={'flex flex-col gap-2'}>
      <Controller
        control={control}
        name="auth_email"
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <Input
            type={'email'}
            label={'Zendesk email'}
            placeholder={'email@zendesk.com'}
            invalidMessage={error?.message}
            value={value}
            isValid={!error?.message}
            onChange={onChange}
            infoTooltip={{
              side: 'right',
              description: <>Email address of the Zendesk user used to generate the API key.</>,
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="api_token"
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <Input
            type={'password'}
            label={'Zendesk API key'}
            placeholder={'------------------------------------------------------'}
            invalidMessage={error?.message}
            value={value}
            isValid={!error?.message}
            onChange={onChange}
            infoTooltip={{
              side: 'right',
              description: (
                <>
                  Check how to generate the API Key{' '}
                  <a
                    href={ZENDESK_HELP_CENTER_URL.API_KEY}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline cursor-pointer"
                  >
                    here →
                  </a>
                </>
              ),
            }}
          />
        )}
      />
    </div>
  );

  return (
    <form
      onSubmit={handleSubmit(submit)}
      id="edit-zendesk-tickets-integration"
      className={'h-full flex flex-col justify-between'}
    >
      {isCheckingAuthStatus && (
        <div
          className={
            'absolute flex items-center justify-center w-full h-full top-0 left-0 bg-ebony_opacity_30 backdrop-blur-sm z-50'
          }
        >
          <div
            className={
              'flex flex-col items-center gap-4 bg-light p-4 rounded-sm-5 text-sm font-semibold'
            }
          >
            <Spinner className={'!w-4 !border-[3px] !border-r-ebony_opacity_50'} />
            <p>Checking Zendesk Connection...</p>
          </div>
        </div>
      )}
      <div className="flex flex-col gap-5 max-h-[510px] overflow-auto pr-4">
        <div className={'flex flex-col gap-2'}>
          <p className={'text-sm font-bold text-ebony'}>Synced tickets</p>
          <SummaryRow
            icon={<CalendarIcon />}
            label={'Solved from'}
            value={moment(item.data.start_from).format('MM/DD/YYYY')}
          />
          <SummaryRow
            icon={<PersonIcon />}
            label={'Solved by'}
            value={item?.data?.agents?.length ? item.data.agents.join(', ') : 'all agents'}
          />
          <SummaryRow
            icon={<TagIcon />}
            label={'Solved with tags'}
            value={item?.data?.tags?.length ? item.data.tags.join(', ') : 'all tags'}
          />
          <SummaryRow
            icon={<GroupIcon />}
            label={'Solved in groups'}
            value={item?.data?.groups?.length ? item.data.groups.join(', ') : 'all groups'}
          />
        </div>
        <div className={'flex flex-row items-center gap-5'}>
          <Controller
            control={control}
            name="autoSync"
            render={({ field: { onChange, value } }) => (
              <div className={'flex items-center gap-2'}>
                <Switch
                  type="accent"
                  id="edit-zendesk-tickets-auto-sync"
                  checked={value}
                  onCheckedChange={onChange}
                  disabled={isTrialPlan || isInactivePlan}
                />
                <label
                  htmlFor="edit-zendesk-tickets-auto-sync"
                  className={'text-sm font-medium text-ebony_opacity_80'}
                >
                  Auto-Sync
                </label>
                {isTrialPlan && (
                  <InputInfoToolTip
                    infoToolTip={{
                      side: 'right',
                      description: <>Unavailable for trial plan</>,
                    }}
                  />
                )}
              </div>
            )}
          />
          {isAutoSync && (
            <Controller
              control={control}
              name="interval"
              render={({ field: { onChange, value } }) => (
                <RadioGroup
                  onValueChange={(interval) => onChange(Number(interval))}
                  defaultValue={String(value)}
                  className="flex flex-row gap-5 items-center"
                >
                  {INTERVAL_OPTIONS.map((option) => (
                    <label
                      key={option.id}
                      htmlFor={`edit-${option.id}`}
                      className="flex cursor-pointer items-center gap-2 text-sm font-medium text-tertiary"
                    >
                      <RadioGroupItem
                        value={String(option.value)}
                        id={`edit-${option.id}`}
                        className={
                          'border-grey_300 text-green_750 data-[state=checked]:border-green_750'
                        }
                      />
                      {option.label}
                    </label>
                  ))}
                </RadioGroup>
              )}
            />
          )}
        </div>
        <div className={'flex flex-col gap-2'}>
          <p className={'text-sm font-bold text-ebony'}>API Details</p>
          <Controller
            control={control}
            name="subdomain"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Input
                type={'text'}
                label={'Zendesk subdomain'}
                placeholder={'.domain.com'}
                invalidMessage={error?.message}
                value={value}
                isValid={!error?.message}
                onChange={(e) => {
                  const res = getSubdomainHelper(e.target.value);
                  if (res?.subdomain) {
                    onChange(res?.subdomain);
                  } else {
                    onChange(e.target.value);
                  }
                }}
                infoTooltip={{ side: 'right', description: 'Link to your Zendesk workspace.' }}
              />
            )}
          />
        </div>
        <div className={'flex flex-col gap-2'}>
          {usesManualToken ? (
            <>
              {manualCredentialFields}
              {orSeparator}
              {zendeskConnectButton}
            </>
          ) : (
            <>
              {zendeskConnectButton}
              {orSeparator}
              <Collapsible
                open={isManualConnectOpen}
                onOpenChange={setIsManualConnectOpen}
                disabled={collector?.data?.auth_type === 'oauth'}
                triggerClassName={COLLAPSIBLE_TRIGGER_CLASS_NAME}
                trigger={
                  <>
                    <span className={'flex-1 text-center'}>Connect manually with API token</span>
                    <img
                      src={ChevronDownIcon}
                      alt={''}
                      width={12.8}
                      height={12.8}
                      className={
                        'transition-transform duration-200 group-data-[state=open]:rotate-180'
                      }
                    />
                  </>
                }
                contentClassName={COLLAPSIBLE_CONTENT_CLASS_NAME}
              >
                <div className={'pt-2'}>{manualCredentialFields}</div>
              </Collapsible>
            </>
          )}
        </div>
      </div>
      <div className="flex justify-center gap-2 mt-5">
        <Button variant="tertiary" type="button" className="w-[100px]" onClick={() => close()}>
          Cancel
        </Button>
        <Button variant="dark" type="submit" className="w-[100px]" disabled={!isDirty}>
          Save
        </Button>
      </div>
    </form>
  );
};
